import React from 'react';
import { Link, useParams } from 'react-router';
import { Badge } from '@rezi/ui/badge';
import { Card } from '@rezi/ui/card';
import { useCategoryById } from '../docsRegistry';
import DocContent from '../components/DocContent';

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green';
    case 'draft':
      return 'yellow';
    case 'archived':
      return 'gray';
    default:
      return 'blue';
  }
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const category = useCategoryById(categoryId);

  if (!category) {
    return (
      <Card className="flex flex-col gap-3">
        <nav className="text-sm text-gray-500">
          <Link className="text-rezi-blue-600 font-semibold" to="/">
            Docs
          </Link>
          <span className="px-2 text-gray-400">/</span>
          <span>Not found</span>
        </nav>
        <div className="text-text text-xl font-semibold">
          Category not found
        </div>
        <p className="text-sm text-gray-500">
          The category ID does not exist in the registry.
        </p>
      </Card>
    );
  }

  const rootDoc = category.rootDoc;
  const Content = rootDoc?.Content;
  const childDocs = category.docs.filter((doc) => doc.id !== rootDoc?.id);
  const inactiveDocs = childDocs.filter(
    (doc) => doc.status.toLowerCase() === 'inactive',
  );
  const activeDocs = childDocs.filter(
    (doc) => doc.status.toLowerCase() !== 'inactive',
  );

  return (
    <section className="flex flex-col gap-6">
      <nav className="text-sm text-gray-500">
        <Link className="text-rezi-blue-600 font-semibold" to="/">
          Docs
        </Link>
        <span className="px-2 text-gray-400">/</span>
        <span>{category.title}</span>
      </nav>

      {rootDoc && Content && (
        <Card className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-text text-2xl font-semibold">
              {rootDoc.title}
            </h1>
          </div>
          <div className="space-y-4 text-sm leading-relaxed text-gray-700 [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:inline [&_h3]:text-base [&_h3]:font-semibold [&_h3]:before:block [&_h3]:before:h-[10px] [&_h3]:after:mr-1 [&_h3]:after:content-[':'] [&_li]:mb-2 [&_p]:mb-2 [&_p]:inline [&_p]:text-base [&_ul]:list-disc [&_ul]:pl-5">
            <DocContent Content={Content} />
          </div>
        </Card>
      )}

      {activeDocs.length > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-text text-lg font-semibold">Active docs</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {activeDocs.map((doc) => {
              const visibility = doc.visibility.toLowerCase();

              return (
                <Link key={doc.id} to={`/docs/${doc.id}`} className="group">
                  <Card className="hover:bg-choice-list-item-hover h-full transition">
                    <div className="flex items-center justify-between">
                      <Badge
                        label={doc.status}
                        color={getStatusColor(doc.status)}
                        size="small"
                        allCaps
                      />
                      <span
                        className={`text-xs font-semibold tracking-[0.12em] uppercase ${
                          visibility === 'confidential'
                            ? 'text-red-500'
                            : 'text-gray-400'
                        }`}
                      >
                        {visibility === 'internal' ||
                        visibility === 'confidential'
                          ? `${doc.visibility} ONLY`
                          : doc.visibility}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-col gap-2">
                      <h2 className="text-text group-hover:text-rezi-blue-600 text-lg font-semibold">
                        {doc.title}
                      </h2>
                      <p className="text-sm text-gray-500">{doc.owner}</p>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {doc.tags.map((tag) => (
                          <Badge
                            key={tag}
                            label={tag}
                            color="light-gray"
                            size="small"
                          />
                        ))}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {inactiveDocs.length > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-text text-lg font-semibold">Inactive docs</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {inactiveDocs.map((doc) => {
              const visibility = doc.visibility.toLowerCase();

              return (
                <Link key={doc.id} to={`/docs/${doc.id}`} className="group">
                  <Card className="hover:bg-choice-list-item-hover h-full transition">
                    <div className="flex items-center justify-between">
                      <Badge
                        label={doc.status}
                        color={getStatusColor(doc.status)}
                        size="small"
                        allCaps
                      />
                      <span
                        className={`text-xs font-semibold tracking-[0.12em] uppercase ${
                          visibility === 'confidential'
                            ? 'text-red-500'
                            : 'text-gray-400'
                        }`}
                      >
                        {visibility === 'internal' ||
                        visibility === 'confidential'
                          ? `${doc.visibility} ONLY`
                          : doc.visibility}
                      </span>
                    </div>
                    <div className="mt-4 flex flex-col gap-2">
                      <h2 className="text-text group-hover:text-rezi-blue-600 text-lg font-semibold">
                        {doc.title}
                      </h2>
                      <p className="text-sm text-gray-500">{doc.owner}</p>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {doc.tags.map((tag) => (
                          <Badge
                            key={tag}
                            label={tag}
                            color="light-gray"
                            size="small"
                          />
                        ))}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default CategoryPage;
